import React, { useState } from "react";
import { Button } from "../../components/template";

/**
 * Scoring:
 * A = 2 points
 * B = 1 point
 * C = 0 points
 */

const scoreMap = {
  0: 2,
  1: 1,
  2: 0,
};

const quizData = [
  {
    question: "When I feel physically or mentally tired, I usually…",
    options: [
      "Allow myself to rest without much guilt",
      "Push myself a bit more before resting",
      "Ignore it and keep going",
    ],
  },
  {
    question: "The way I speak to myself internally is usually…",
    options: [
      "Kind, encouraging, or understanding",
      "A mix of supportive and critical",
      "Mostly harsh or demanding",
    ],
  },
  {
    question: "When I need to say “no” to someone, I usually…",
    options: [
      "Say it respectfully and clearly",
      "Struggle but sometimes manage",
      "Avoid it and put others first",
    ],
  },
  {
    question: "My sense of self-worth feels…",
    options: [
      "Fairly stable, regardless of others’ opinions",
      "Influenced by how others respond to me",
      "Largely dependent on validation from others",
    ],
  },
  {
    question: "When I achieve something (big or small), I…",
    options: [
      "Acknowledge and appreciate myself",
      "Feel good briefly and move on quickly",
      "Rarely pause to appreciate it",
    ],
  },
  {
    question: "When I make a mistake, my natural response is to…",
    options: [
      "Forgive myself and learn from it",
      "Replay it in my head for some time",
      "Blame or shame myself",
    ],
  },
  {
    question: "I prioritise my own needs (rest, health, emotions)…",
    options: ["Regularly", "Occasionally", "Rarely"],
  },
  {
    question: "Being alone with myself feels…",
    options: [
      "Comfortable or nourishing",
      "Okay for short periods",
      "Uncomfortable or unsettling",
    ],
  },
  {
    question: "I honour my physical and emotional limits by…",
    options: [
      "Listening to them most of the time",
      "Noticing them but often crossing them",
      "Ignoring them",
    ],
  },
  {
    question: "Deep down, I believe that I deserve care, rest, and love…",
    options: [
      "Yes, without conditions",
      "Sometimes, but not always",
      "Rarely or only when I “earn” it",
    ],
  },
];

/* -------------------- INTRO PAGE -------------------- */

const IntroPage = ({ onStart }) => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-light px-6 py-10 text-center">
    <div className="max-w-lg bg-white p-6 rounded-2xl shadow-md">
      <h1 className="text-2xl font-bold text-primary mb-4">
        Self-Love Self-Assessment
      </h1>

      <p className="text-gray-700">
        As I read each statement, I’ll notice how I usually treat myself,
        especially during ordinary, imperfect days.
      </p>

      <p className="mt-3 text-gray-700">
        I’ll answer honestly —
        <strong> this is about awareness, not judgment.</strong>
      </p>

      <Button
        variant="primary"
        size="md"
        className="mt-6"
        text="Start Quiz"
        onClick={onStart}
      />
    </div>
  </div>
);

/* -------------------- QUIZ PAGE -------------------- */

const QuizPage = ({ onComplete }) => {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState(Array(quizData.length).fill(null));
  const [error, setError] = useState("");

  const handleSelect = (index) => {
    const updated = [...answers];
    updated[current] = index;
    setAnswers(updated);
    setError("");
  };

  const handleNext = () => {
    if (answers[current] === null) {
      setError("Please select an option to proceed.");
      return;
    }

    if (current < quizData.length - 1) {
      setCurrent(current + 1);
    } else {
      onComplete(answers);
    }
  };

  const handlePrev = () => {
    setError("");
    if (current > 0) setCurrent(current - 1);
  };

  const progress = ((current + 1) / quizData.length) * 100;

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-light px-6 py-10">
      <div className="w-full max-w-2xl bg-white p-8 rounded-2xl shadow-lg text-center">
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">
            Question {current + 1} of {quizData.length}
          </p>
          <div className="w-full bg-gray-200 h-2 rounded-full">
            <div
              className="bg-primary h-2 rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <p className="text-[#578d48] font-bold text-xl mb-4">
          {quizData[current].question}
        </p>

        <div className="space-y-3">
          {quizData[current].options.map((opt, i) => (
            <label
              key={i}
              className={`flex items-center justify-center p-3 rounded-lg cursor-pointer transition
                ${
                  answers[current] === i
                    ? "bg-primary text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
            >
              <input
                type="radio"
                checked={answers[current] === i}
                onChange={() => handleSelect(i)}
                className="hidden"
              />
              <span className="text-sm">{opt}</span>
            </label>
          ))}
        </div>

        {error && <p className="mt-4 text-red-500 text-sm">{error}</p>}
      </div>

      <div className="flex mt-6 space-x-4">
        {current > 0 && (
          <Button text="Previous" variant="whitish" onClick={handlePrev} />
        )}
        <Button
          text={current === quizData.length - 1 ? "Submit" : "Next"}
          variant="primary"
          onClick={handleNext}
        />
      </div>
    </div>
  );
};

/* -------------------- RESULT PAGE -------------------- */

const ResultPage = ({ answers, onRetake }) => {
  const totalScore = answers.reduce(
    (sum, ans) => sum + scoreMap[ans],
    0
  );

  let resultText = "";

  if (totalScore >= 15) {
    resultText = `💗 : Healthy Self-Love
You generally treat yourself with kindness, respect, and care

You’re able to rest, set boundaries, and self-validate

Focus: continue nurturing this relationship with yourself`;
  } else if (totalScore >= 8) {
    resultText = `🌱 : Growing Self-Love
You are aware of self-care but may struggle with guilt or self-criticism

Old patterns of over-giving or perfectionism may show up

Focus: consistent boundaries and kinder self-talk`;
  } else {
    resultText = `🌧️ : Self-Love Needs Attention
You may be neglecting your own needs or being very hard on yourself

This often comes from conditioning, not personal failure

Focus: start small — rest, compassion, and permission to receive care`;
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-light px-6 py-10 text-center">
      <div className="max-w-lg bg-white p-6 rounded-2xl shadow-md">
        <h2 className="text-2xl font-bold text-secondary mb-4">
          Your Self-Love Score
        </h2>

        <p className="text-lg font-semibold text-primary">
          Total Score: {totalScore} / 20
        </p>

        <pre className="whitespace-pre-wrap text-left text-gray-700 mt-4">
          {resultText}
        </pre>

        <Button
          variant="primary"
          size="md"
          className="mt-6"
          text="Retake Quiz"
          onClick={onRetake}
        />
      </div>
    </div>
  );
};

/* -------------------- MAIN COMPONENT -------------------- */

const SelfLoveQuiz = () => {
  const [started, setStarted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [answers, setAnswers] = useState(null);

  return (
    <>
      {!started ? (
        <IntroPage onStart={() => setStarted(true)} />
      ) : completed ? (
        <ResultPage
          answers={answers}
          onRetake={() => {
            setStarted(false);
            setCompleted(false);
            setAnswers(null);
          }}
        />
      ) : (
        <QuizPage
          onComplete={(ans) => {
            setAnswers(ans);
            setCompleted(true);
          }}
        />
      )}
    </>
  );
};

export default SelfLoveQuiz;
